import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { fetchEvents } from "redux/features/Admin/Events/eventSlice";
import { EASING } from "../../utils/easingUtils";
import {
  Briefcase,
  Heart,
  PartyPopper,
  GraduationCap,
  Palette,
  Dumbbell,
  Music,
  Crown,
  ArrowRight,
  Calendar,
} from "lucide-react";

interface ModernCategoriesSectionProps {
  className?: string;
}

const ModernCategoriesSection: React.FC<ModernCategoriesSectionProps> = ({ className = "" }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const dispatch = useAppDispatch();
  const eventState = useAppSelector((state) => state.featureEvent);

  // Fetch events on component mount
  useEffect(() => {
    dispatch(fetchEvents());
  }, [dispatch]);

  // Icon mapping for different event types
  const getEventIcon = (eventName: string) => {
    const name = eventName.toLowerCase();
    if (
      name.includes("business") ||
      name.includes("professionnel") ||
      name.includes("séminaire") ||
      name.includes("conférence")
    )
      return Briefcase;
    if (name.includes("mariage") || name.includes("wedding")) return Heart;
    if (name.includes("fête") || name.includes("fete") || name.includes("anniversaire") || name.includes("party"))
      return PartyPopper;
    if (
      name.includes("formation") ||
      name.includes("cours") ||
      name.includes("atelier") ||
      name.includes("education")
    )
      return GraduationCap;
    if (
      name.includes("culturel") ||
      name.includes("culture") ||
      name.includes("art") ||
      name.includes("exposition")
    )
      return Palette;
    if (name.includes("sport") || name.includes("fitness") || name.includes("yoga")) return Dumbbell;
    if (name.includes("concert") || name.includes("musique") || name.includes("music")) return Music;
    if (name.includes("luxe") || name.includes("gala") || name.includes("prestige")) return Crown;
    return Calendar; // Default icon
  };

  // Color mapping for different event types
  const getEventColor = (eventName: string, index: number = 0) => {
    const name = eventName.toLowerCase();

    // Specific matches
    if (
      name.includes("business") ||
      name.includes("professionnel") ||
      name.includes("entreprise") ||
      name.includes("corporate")
    )
      return {
        gradient: "from-[#4663ac] to-[#c1d8f0]",
        bg: "bg-[#4663ac]/10 dark:bg-[#4663ac]/20",
      };
    if (name.includes("mariage") || name.includes("wedding") || name.includes("noce"))
      return {
        gradient: "from-[#f79dcb] to-[#f7e67c]",
        bg: "bg-[#f79dcb]/10 dark:bg-[#f79dcb]/20",
      };
    if (
      name.includes("fête") ||
      name.includes("fete") ||
      name.includes("anniversaire") ||
      name.includes("party") ||
      name.includes("soirée") ||
      name.includes("celebration")
    )
      return {
        gradient: "from-[#4663ac] to-[#f79dcb]",
        bg: "bg-[#4663ac]/10 dark:bg-[#4663ac]/20",
      };
    if (
      name.includes("formation") ||
      name.includes("cours") ||
      name.includes("atelier") ||
      name.includes("education") ||
      name.includes("enseignement") ||
      name.includes("apprentissage")
    )
      return {
        gradient: "from-[#c1d8f0] to-[#f7e67c]",
        bg: "bg-[#c1d8f0]/10 dark:bg-[#c1d8f0]/20",
      };
    if (
      name.includes("culturel") ||
      name.includes("culture") ||
      name.includes("art") ||
      name.includes("exposition") ||
      name.includes("vernissage")
    )
      return {
        gradient: "from-[#f7e67c] to-[#f79dcb]",
        bg: "bg-[#f7e67c]/10 dark:bg-[#f7e67c]/20",
      };
    if (
      name.includes("sport") ||
      name.includes("fitness") ||
      name.includes("yoga") ||
      name.includes("musculation") ||
      name.includes("gym")
    )
      return {
        gradient: "from-[#4663ac] to-[#c1d8f0]",
        bg: "bg-[#4663ac]/10 dark:bg-[#4663ac]/20",
      };
    if (
      name.includes("concert") ||
      name.includes("musique") ||
      name.includes("music") ||
      name.includes("spectacle") ||
      name.includes("show")
    )
      return {
        gradient: "from-[#c1d8f0] to-[#f79dcb]",
        bg: "bg-[#c1d8f0]/10 dark:bg-[#c1d8f0]/20",
      };
    if (
      name.includes("luxe") ||
      name.includes("gala") ||
      name.includes("prestige") ||
      name.includes("haut de gamme")
    )
      return {
        gradient: "from-[#f7e67c] to-[#4663ac]",
        bg: "bg-[#f7e67c]/10 dark:bg-[#f7e67c]/20",
      };

    // If no match, use a rotating color scheme based on index
    const colors = [
      { gradient: "from-[#4663ac] to-[#c1d8f0]", bg: "bg-[#4663ac]/10 dark:bg-[#4663ac]/20" },
      { gradient: "from-[#c1d8f0] to-[#f7e67c]", bg: "bg-[#c1d8f0]/10 dark:bg-[#c1d8f0]/20" },
      { gradient: "from-[#4663ac] to-[#f79dcb]", bg: "bg-[#4663ac]/10 dark:bg-[#4663ac]/20" },
      { gradient: "from-[#f79dcb] to-[#f7e67c]", bg: "bg-[#f79dcb]/10 dark:bg-[#f79dcb]/20" },
      { gradient: "from-[#f7e67c] to-[#f79dcb]", bg: "bg-[#f7e67c]/10 dark:bg-[#f7e67c]/20" },
      { gradient: "from-[#4663ac] to-[#c1d8f0]", bg: "bg-[#4663ac]/10 dark:bg-[#4663ac]/20" },
      { gradient: "from-[#c1d8f0] to-[#f79dcb]", bg: "bg-[#c1d8f0]/10 dark:bg-[#c1d8f0]/20" },
      { gradient: "from-[#f7e67c] to-[#4663ac]", bg: "bg-[#f7e67c]/10 dark:bg-[#f7e67c]/20" },
    ];

    return colors[index % colors.length];
  };

  // Transform API events to categories format
  const transformEventsToCategories = () => {
    if (!eventState.events || !Array.isArray(eventState.events)) {
      return [];
    }

    return eventState.events.map((event: any, index: number) => {
      const colors = getEventColor(event.designation, index);
      return {
        id: event.id || index + 1,
        name: event.designation,
        icon: getEventIcon(event.designation),
        color: colors.gradient,
        bgColor: colors.bg,
        description: `Événements de type ${event.designation.toLowerCase()}`,
        count: "Disponible", // You could add a count from API if available
      };
    });
  };

  // Use ONLY real events from database
  const displayCategories = transformEventsToCategories();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const hexVariants = {
    hidden: {
      opacity: 0,
      scale: 0,
      rotateY: -90,
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotateY: 0,
      transition: {
        duration: 0.6,
        ease: EASING.backOut,
      },
    },
  };

  const createHexagonPath = () => {
    const width = 120;
    const height = 104;
    const points = [];
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI) / 3;
      const x = Math.cos(angle) * (width / 2) + width / 2;
      const y = Math.sin(angle) * (height / 2) + height / 2;
      points.push(`${x},${y}`);
    }
    return `polygon(${points.join(" ")})`;
  };

  return (
    <div
      className={`py-24 bg-gradient-to-b from-[#4663ac]/10 to-white dark:from-[#4663ac]/20 dark:to-gray-900 ${className}`}
      ref={ref}
    >
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="mb-6">
            <span className="text-[#4663ac] font-semibold uppercase tracking-wide text-sm">
              Types d'événements
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Trouvez l'espace pour{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4663ac] to-[#c1d8f0]">
              votre événement
            </span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Chaque occasion mérite un lieu d'exception. Découvrez notre sélection d'espaces adaptés à tous vos
            projets
          </p>
        </motion.div>

        {/* Hexagonal Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8 max-w-6xl mx-auto mb-12"
        >
          {displayCategories.map((category, index) => {
            const Icon = category.icon;
            return (
              <motion.div
                key={category.id}
                variants={hexVariants}
                whileHover={{
                  scale: 1.1,
                  rotateZ: 5,
                  y: -10,
                }}
                whileTap={{ scale: 0.95 }}
                className={`relative group cursor-pointer ${index === 1 || index === 2 ? "md:mt-12" : ""} ${index === 5 || index === 6 ? "md:mt-12" : ""}`}
              >
                {/* Hexagon Background */}
                <div
                  className={`w-32 h-28 mx-auto relative ${category.bgColor} transition-all duration-500 group-hover:shadow-2xl`}
                  style={{
                    clipPath: createHexagonPath(),
                  }}
                >
                  {/* Gradient Overlay on Hover */}
                  <div
                    className={`absolute inset-0 opacity-0 group-hover:opacity-90 bg-gradient-to-br ${category.color} transition-opacity duration-500`}
                    style={{
                      clipPath: createHexagonPath(),
                    }}
                  />

                  {/* Icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Icon className="w-8 h-8 text-gray-600 dark:text-gray-400 group-hover:text-white transition-colors duration-500 z-10" />
                  </div>
                </div>

                {/* Category Info */}
                <div className="text-center mt-4 group-hover:transform group-hover:scale-105 transition-transform duration-300">
                  <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1 group-hover:text-[#4663ac] dark:group-hover:text-[#c1d8f0] transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{category.count}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {category.description}
                  </p>
                </div>

                {/* Hover Effect Particles */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  {[...Array(6)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-1 h-1 bg-white rounded-full"
                      style={{
                        top: `${20 + Math.random() * 60}%`,
                        left: `${20 + Math.random() * 60}%`,
                      }}
                      animate={{
                        y: [0, -20, 0],
                        opacity: [0, 1, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: Math.random() * 2,
                      }}
                    />
                  ))}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1, duration: 0.8 }}
          className="text-center"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center space-x-3 bg-transparent border-2 border-[#4663ac] text-[#4663ac] hover:bg-[#4663ac] hover:text-white px-8 py-3 rounded-full font-semibold transition-all duration-300"
          >
            <span>Explorer toutes les catégories</span>
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default ModernCategoriesSection;
